import { INTERVIEWS, getLocalInterviewBySlug, getLocalInterviews } from '../data/interviews';
import {
  createInterview,
  deleteInterview as deleteInterviewDoc,
  getAllInterviews,
  getInterviewBySlug as getRemoteInterviewBySlug,
  updateInterview as updateInterviewDoc,
  upsertInterview
} from './firestoreService';
import { toCmsInterview } from '../utils/interviewCms';

const isPublished = (item) => item && item.status === 'published';

const sortByOrder = (items) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const omitUndefined = (value) => {
  if (Array.isArray(value)) return value.map(omitUndefined);
  if (
    value &&
    typeof value === 'object' &&
    !(value instanceof Date) &&
    typeof value.toDate !== 'function'
  ) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, nested]) => nested !== undefined)
        .map(([key, nested]) => [key, omitUndefined(nested)])
    );
  }
  return value;
};

const toSavePayload = (interview) => {
  const { id, createdAt, updatedAt, ...rest } = interview;
  const joinYearRaw = rest.joinYear;
  const joinYear =
    joinYearRaw === '' || joinYearRaw == null || Number.isNaN(Number(joinYearRaw))
      ? null
      : Number(joinYearRaw);

  return JSON.parse(JSON.stringify(omitUndefined({
    ...rest,
    joinYear
  })));
};

const isPermissionError = (message = '') =>
  /insufficient permissions|permission-denied/i.test(message);

const formatSeedFailure = (errors) => {
  if (!errors.length) return 'インタビューの投入に失敗しました';
  if (errors.some((item) => isPermissionError(item.error))) {
    return 'Firestore の interviews コレクションへの書き込みが拒否されました。プロジェクトルートで firebase deploy --only firestore:rules を実行してください。';
  }
  return errors.map((item) => `${item.slug}: ${item.error}`).join(' / ');
};

export const getPublishedInterviews = async () => {
  try {
    const remote = await getAllInterviews();
    const published = sortByOrder(remote.filter(isPublished));
    if (published.length > 0) {
      return published;
    }
  } catch (error) {
    console.warn('Falling back to local interview data:', error.message);
  }
  return getLocalInterviews();
};

export const getInterviewBySlug = async (slug) => {
  try {
    const remote = await getRemoteInterviewBySlug(slug);
    if (isPublished(remote)) {
      return remote;
    }
  } catch (error) {
    console.warn('Falling back to local interview data:', error.message);
  }
  return getLocalInterviewBySlug(slug);
};

export const getAdminInterviews = async () => {
  const remote = await getAllInterviews();
  return sortByOrder(remote);
};

export const getAdminInterviewBySlug = async (slug) => {
  return getRemoteInterviewBySlug(slug);
};

export const saveInterview = async (interview, { isNew } = {}) => {
  const slug = String(interview.slug || '').trim();
  if (!slug) {
    throw new Error('スラッグは必須です');
  }
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    throw new Error('スラッグは半角英数字とハイフンのみです（例: hyoga-kishikawa）');
  }

  const payload = toSavePayload({ ...interview, slug });

  if (isNew) {
    const existing = await getRemoteInterviewBySlug(slug);
    if (existing) {
      throw new Error('このスラッグは既に使われています');
    }
    await createInterview(payload);
    return slug;
  }

  await updateInterviewDoc(slug, payload);
  return slug;
};

export const removeInterview = async (slug) => {
  await deleteInterviewDoc(slug);
};

export const updateInterviewOrders = async (orderedSlugs) => {
  await Promise.all(
    orderedSlugs.map((slug, index) => updateInterviewDoc(slug, { order: index + 1 }))
  );
};

export const getFeaturedInterviews = async () => {
  const interviews = await getPublishedInterviews();
  return interviews
    .filter((item) => item.hasArticle && item.featuredOnHome !== false)
    .slice(0, 4);
};

export const getRelatedInterviews = async (slug, limit = 3) => {
  const interviews = await getPublishedInterviews();
  return interviews.filter((item) => item.slug !== slug && item.hasArticle).slice(0, limit);
};

export const seedInterviews = async (overwrite = false) => {
  const created = [];
  const skipped = [];
  const errors = [];

  for (const interview of INTERVIEWS) {
    try {
      const result = await upsertInterview(toSavePayload(toCmsInterview(interview)), overwrite);
      if (result.skipped) {
        skipped.push(interview.slug);
      } else {
        created.push(interview.slug);
      }
    } catch (error) {
      console.error('Failed to seed interview', interview.slug, error);
      errors.push({ slug: interview.slug, error: error.message });
    }
  }

  const success = errors.length === 0;
  return {
    success,
    created,
    skipped,
    errors,
    total: INTERVIEWS.length,
    message: success
      ? overwrite
        ? `インタビューを上書き投入しました（${created.length}件）`
        : `インタビューを投入しました（作成 ${created.length}件 / スキップ ${skipped.length}件）`
      : undefined,
    error: success ? undefined : formatSeedFailure(errors)
  };
};
